/* Nefes gün görünümü. The grid is the notebook page: hours down,
   danışmanlar across, the whole day on one laptop screen, a tablet
   scrolls vertically only. Colour never carries a state alone — the
   chip's word is always next to it (day-view.md §6). Light only: the
   desk screen, daytime. */

:root {
  /* The ground and lines of the drawer's look (the pay sheet's A3
     tokens), the whole app on them since the day view moved over. */
  --bg: #f2f3f5;
  --paper: #ffffff;
  --ink: #16191c;
  --muted: #5f656b;
  --line: #e3e5e8;
  --line-soft: #eceef0;
  --chip: #e9ebee;
  --chip-hover: #dfe2e6;
  --cur: #a9afb5;
  --orange: #e8920c;
  /* The accent is the logo's blue (#00B0EC). Fills carry dark ink —
     white on it is too faint to read; the deep shade is the same hue
     where the accent is text on paper (links). */
  --brand: #00B0EC;
  --brand-ink: #0b2a3a;
  --brand-deep: #00739f;
  --ok: #176e2e;
  --ok-bg: #e6f4ea;
  --warn: #8a5a00;
  --warn-bg: #fff4d6;
  --bad: #a1201d;
  --bad-bg: #fbe9e7;
  --grey-bg: #ececea;
  --blue-bg: #e7eefb;
  --blue: #1d4f9c;
  --now: #d6453d;
  /* One hour of the grid. Sized so the configured day fits the
     viewport under the header and the nav; never below 58px, which
     keeps a 50-minute cell three lines tall (time row + two names). */
  --hour-h: clamp(58px, calc((100vh - 190px) / var(--hours, 12)), 84px);
  color-scheme: light;
}

* { box-sizing: border-box; }
html, body, #root { height: 100%; }
body {
  margin: 0;
  background: var(--bg);
  color: var(--ink);
  font: 15px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}
button, input { font: inherit; color: inherit; }
button {
  padding: .45rem .9rem;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  cursor: pointer;
}
button:hover:not(:disabled) { border-color: #b9b6ae; }
button:disabled { opacity: .55; cursor: default; }
button.primary {
  background: var(--brand);
  border-color: var(--brand);
  color: var(--brand-ink);
  font-weight: 600;
}
input[type="date"] {
  padding: .35rem .5rem;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
}
h1, h2, h3 { margin: 0; font-weight: 650; }
h1 { font-size: 1.5rem; }
h2 { font-size: 1.1rem; }
.muted { color: var(--muted); }
.small { font-size: .85rem; }
.error { color: var(--bad); }
.spacer { flex: 1; }

.app { min-height: 100%; display: flex; flex-direction: column; }
.foot { padding: .75rem 16px; text-align: center; }

/* Sign-in / not-registered / error */
.card-page { flex: 1; display: grid; place-items: center; padding: 2rem 16px; }
.card {
  width: min(28rem, 100%);
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 1.75rem;
  display: grid;
  gap: 1rem;
}

/* Shell — the drawer's look (C, Onur 14.10.2026): a grey ground,
   pills and round icon buttons, white only where something sits. */
.top {
  display: flex;
  align-items: center;
  gap: .6rem;
  padding: .6rem 16px .2rem;
  background: var(--bg);
  position: sticky;
  top: 0;
  z-index: 3;
}
.top button {
  border: 0; border-radius: 999px; background: var(--paper);
  display: inline-flex; align-items: center; gap: 7px; white-space: nowrap;
}
.top button:hover:not(:disabled) { background: var(--chip); }
.top .round { justify-content: center; } /* the eye and Çıkış: icon only */
.brand { height: 1.3rem; width: auto; display: block; } /* the logo, in its own blue */
.who { max-width: 40vw; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Day */
.day { flex: 1; padding: .4rem 16px 1.5rem; display: grid; gap: .6rem; align-content: start; }
.day-nav { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
.day-title { font-size: 1.55rem; letter-spacing: -.015em; line-height: 1.15; font-weight: 700; padding: 0 .15rem; }
.day-alerts { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
.round {
  width: 40px; height: 40px; padding: 0; border: 0; border-radius: 50%; flex-shrink: 0;
  background: var(--paper); display: inline-grid; place-items: center;
}
.round:hover:not(:disabled) { background: var(--chip); }
.round[aria-busy="true"] .sd-i { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
/* The date field hides under its round calendar button: the browser's
   calendar opens from the button's place. */
.date-pick { position: relative; display: inline-grid; }
.date-pick input {
  position: absolute; inset: 0; width: 100%; height: 100%;
  padding: 0; border: 0; opacity: 0; pointer-events: none;
}
.pill {
  display: inline-flex; align-items: center; gap: 7px; border: 0; border-radius: 999px;
  padding: 7px 14px; font-size: .9rem; white-space: nowrap; background: var(--paper);
}
button.pill:hover:not(:disabled) { background: var(--chip); }
.pill .soft { color: var(--muted); }
.pill.soft { background: var(--chip); }
.pill.warn { background: var(--warn-bg); color: var(--warn); font-weight: 600; }
button.pill.warn:hover { background: #fbe7bd; }
.pill.bad { background: var(--bad-bg); color: var(--bad); font-weight: 600; white-space: normal; }
.banner { margin: 0; padding: .5rem .9rem; border-radius: 10px; }
.banner.warn { background: var(--warn-bg); color: var(--warn); }
.banner.error { background: var(--bad-bg); color: var(--bad); }
.list { background: var(--paper); border-radius: 12px; padding: .5rem .9rem; }

/* ── The day's money and a danışman's day (their own drawers) ──
   One card per subject, one fact per row: the label left, the figure
   right. Built on the seans drawer's sheet (.seans-sheet). */
.day-money .dm-title { margin: 0; flex: 1; }
.dm-tiles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.dm-tile { background: var(--paper); border-radius: 16px; padding: 12px 14px; display: grid; gap: 2px; align-content: start; min-width: 0; }
.dm-label { color: var(--muted); font-size: .88rem; }
.dm-fig { font-size: 1.6rem; line-height: 1.15; letter-spacing: -.02em; font-weight: 700; font-variant-numeric: tabular-nums; }
.dm-fig .cur { color: var(--cur); font-weight: 600; }
.dm-fig.bad { color: var(--bad); }
.dm-tile-sub { color: var(--muted); font-size: .84rem; font-variant-numeric: tabular-nums; }
.dm-card { background: var(--paper); border-radius: 16px; padding: 14px 16px 4px; display: grid; }
.dm-card-head {
  margin: 0 0 2px; font-size: 1rem; display: flex; justify-content: space-between;
  align-items: baseline; gap: 8px;
}
.dm-card-head small { font-weight: 400; color: var(--muted); font-size: .86rem; font-variant-numeric: tabular-nums; }
.dm-rows { list-style: none; margin: 0; padding: 0; }
.dm-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 9px 0; }
.dm-row + .dm-row { border-top: 1px solid var(--line-soft); }
.dm-row.sub { padding: 3px 0 3px 14px; border-top: 0; font-size: .9rem; color: var(--muted); }
.dm-row.sub .dm-v { font-weight: 500; color: var(--muted); }
.dm-k { min-width: 0; display: grid; overflow-wrap: anywhere; }
.dm-k small { color: var(--muted); font-size: .82rem; }
.dm-k.soft { color: var(--muted); }
.dm-v { font-weight: 600; font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; justify-content: flex-end; }
.dm-v .sd-i { color: var(--cur); }
.dm-v.ok { color: var(--ok); }
.dm-v.bad { color: var(--bad); }
.dm-v.soft { color: var(--muted); font-weight: 400; }
.seans-sheet button.dm-link {
  grid-column: 1 / -1; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px;
  align-items: center; text-align: left; width: 100%; padding: 0; background: none; border-radius: 8px;
}
.dm-row:has(> .dm-link) { display: block; }
.seans-sheet button.dm-link:hover .dm-k { text-decoration: underline; text-decoration-color: var(--cur); }
.dm-foot { padding: 4px 0 10px; display: grid; gap: 4px; color: var(--muted); font-size: .84rem; }
.dm-foot p { margin: 0; }
.dm-foot .warn { color: var(--warn); font-weight: 600; }
.dm-chip {
  display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 4px 11px;
  font-size: .82rem; background: var(--chip); color: var(--muted); white-space: nowrap;
}
.dm-chip.ok { background: var(--ok-bg); color: var(--ok); font-weight: 600; }
.dm-chip.warn { background: var(--warn-bg); color: var(--warn); font-weight: 600; }
/* The Kasa drawer (Onur, 22.10.2026): a row of "Bugün verilecek" with
   its Ver at the right, the lines under a movement, and the total. */
.dm-row.pay, .dm-row.pay:has(> .dm-link) {
  display: grid; grid-template-columns: minmax(0, 1fr) 56px;
}
.seans-sheet .dm-row.pay > button.dm-link { grid-column: auto; }
.dm-main { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px; align-items: center; min-width: 0; }
.seans-sheet button.dm-give {
  width: 56px; border-radius: 999px; padding: 7px 0; background: var(--p-dark); color: #fff;
  font-size: .875rem; font-weight: 600; text-align: center;
}
.seans-sheet button.dm-give:hover:not(:disabled) { background: #000; }
.dm-row.sub.end { padding-bottom: 9px; }
.dm-row.total { border-top: 1px solid var(--line); padding: 11px 0 12px; }
.dm-row.total .dm-k { font-weight: 700; }
.dm-row.total .dm-v { font-weight: 700; font-size: 1.06rem; }
.seans-sheet .pd-hero button.pd-go { margin-top: 12px; }

/* Her head: the photo big, her name and her day beside it (Onur,
   3.11.2026); below the Kasa chip when she was opened from the Kasa drawer. */
.pd-who { display: flex; align-items: center; gap: 16px; min-width: 0; }
.seans-sheet .sd-head > .pd-who { margin-top: 10px; }
.pd-who-text { display: grid; gap: 2px; min-width: 0; }
.seans-sheet .pd-who .pd-name { margin: 0; overflow-wrap: anywhere; }
.pd-span { white-space: nowrap; } /* a narrow head breaks before the hours, not inside */
.seans-sheet .sd-who > .pd-who { flex: 1; }
.seans-sheet .sd-who:has(> .pd-who) { align-items: flex-start; }
.pd-hero { padding: 14px 16px 16px; gap: 2px; }
.pd-hero small { color: var(--muted); font-size: .86rem; }
.pd-fig { font-size: 2.3rem; }
.pd-row { display: block; }
.pd-seans {
  display: grid; grid-template-columns: 3.1rem minmax(0, 1fr) 22px; gap: 10px; align-items: center;
  width: 100%; text-align: left;
}
.seans-sheet button.pd-seans { padding: 0; background: none; }
.seans-sheet button.pd-seans:hover .pd-names { text-decoration: underline; text-decoration-color: var(--cur); }
.pd-time { font-weight: 600; font-variant-numeric: tabular-nums; }
.pd-names { min-width: 0; display: grid; overflow: hidden; }
.pd-names small { color: var(--muted); font-size: .82rem; }
.pd-n { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pd-names.off { color: var(--muted); }
.pd-names.off .pd-n { text-decoration: line-through; }
.pd-seans .gc-sign { position: static; }
/* The day's WhatsApp send: a tick, the time, the child; a row that
   cannot be sent is greyed with its reason on the right — the session
   box's icon where the grid has one (Onur, 31.10.2026), else the word.
   The speech bubble sits in the round sign's width, so the icons line
   up, and grows with the drawer's larger text. */
.wd-pick { display: grid; grid-template-columns: auto auto minmax(0, 1fr); gap: 10px; align-items: center; min-width: 0; cursor: pointer; }
.wd-pick input { width: 18px; height: 18px; margin: 0; accent-color: var(--cur); }
.wd-pick.off { color: var(--muted); cursor: default; }
.wa-day .gc-sign { position: static; }
.wa-day .gc-mark { width: 22px; justify-content: center; }
.wa-day .gc-mark .sd-i { width: 16px; height: 16px; }
.wa-day .sd-go { margin-top: 4px; }

/* Gizli mod: the switch is the one sign of it — lit while it is on. */
.top .blind-toggle.on, .blind-toggle.on { background: var(--warn-bg); color: var(--warn); font-weight: 600; }

/* ── The grid ─────────────────────────────────────────── */

.grid {
  --gutter: 3.25rem;
  overflow-x: auto; /* many columns on a narrow screen: scroll sideways */
  font-size: 12.5px;
  line-height: 1.3;
}
.grid-head, .grid-body {
  display: grid;
  grid-template-columns: var(--gutter) repeat(var(--cols, 1), minmax(8.5rem, 1fr));
  min-width: max-content;
  /* A danışman's column is at most a third of the screen (Onur,
     14.10.2026): one or two columns no longer stretch across it. */
  max-width: calc(var(--gutter) + var(--cols, 1) * max(33vw, 16rem));
}
.grid-head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bg);
}
.col-head {
  padding: .4rem .6rem;
  display: flex;
  align-items: center;
  gap: .45rem;
  flex-wrap: wrap;
  min-height: 2.4rem;
}
.col-name { font-weight: 650; font-size: .98rem; }

/* A danışman's round photo, or her initials (ui/Avatar.tsx). */
.avatar {
  flex: none; display: inline-grid; place-items: center; overflow: hidden;
  width: 1.75rem; height: 1.75rem; border-radius: 50%;
  background: var(--chip, #e6e9ed); color: var(--muted, #5f666d);
  font-size: .72rem; font-weight: 700; letter-spacing: .02em; line-height: 1;
}
.avatar.lg { width: 5rem; height: 5rem; font-size: 1.6rem; }
.avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.with-avatar { display: flex; align-items: center; gap: .6rem; min-width: 0; }
.seans-sheet .sd-when .avatar { width: 1.5rem; height: 1.5rem; font-size: .66rem; margin-left: 2px; }
.col-count { background: var(--paper); border-radius: 999px; padding: 0 .55rem; font-size: .78rem; font-weight: 600; color: var(--muted); }
.col-btn {
  display: inline-flex; align-items: center; gap: .45rem; border: 0; border-radius: 999px;
  padding: .3rem .55rem .3rem .65rem; margin-left: -.65rem; background: none; min-width: 0;
}
.col-btn:hover:not(:disabled) { background: var(--paper); }
.col-btn:hover .col-count { background: var(--chip); }
.col-btn > .sd-i { color: var(--cur); }

/* No line across the hours (Onur, 14.10.2026: they read like a
   spreadsheet): the hour is a big number centred beside its hour's seans, in
   a soft grey — "09", not "09:00". */
.grid-body {
  position: relative;
  height: calc(var(--hour-h) * var(--hours, 12));
}
.grid-body .gutter { position: relative; }

/* On a phone, two or more danışmanlar no longer squeeze into the width
   (Onur, 17.10.2026): a column takes most of the screen, the next one
   peeks in at the edge, a sideways swipe brings it in and stops on a
   column, and the hours stay put at the left. One danışman keeps the
   whole width. */
@media (max-width: 600px) {
  .grid.multi {
    container-type: inline-size;
    scroll-snap-type: x mandatory;
    scroll-padding-left: var(--gutter);
    overscroll-behavior-x: contain;
  }
  .grid.multi .grid-head, .grid.multi .grid-body {
    grid-template-columns: var(--gutter) repeat(var(--cols), calc((100cqw - var(--gutter)) * .84));
    max-width: none;
  }
  .grid.multi .gutter {
    position: sticky;
    left: 0;
    z-index: 3;
    background: var(--bg);
  }
  .grid.multi .col, .grid.multi .col-head { scroll-snap-align: start; }
}
.hour-label {
  position: absolute;
  right: .5rem;
  /* Centred on a 50-minute seans starting on the hour, not on the whole
     hour band: a cell ends 10 minutes short of the next hour, so the
     band's middle read low beside it (Onur, 15.10.2026). */
  height: calc(var(--hour-h) * 50 / 60);
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -.02em;
  color: #b8bcc1; /* a quiet backdrop, well under the names */
  font-variant-numeric: tabular-nums;
  text-box: trim-both cap alphabetic; /* centre the digits' ink, not the font's line box */
}
.col {
  position: relative;
  border-left: 1px solid var(--line);
}
.col-dead {
  background: repeating-linear-gradient(135deg, transparent 0 9px, rgba(0,0,0,.035) 9px 18px);
}
.now-line {
  position: absolute;
  left: var(--gutter);
  right: 0;
  height: 0;
  border-top: 2px solid var(--now);
  z-index: 1;
  pointer-events: none;
}
.now-line::before {
  content: "";
  position: absolute;
  left: -5px;
  top: -6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--now);
}

/* Cells: the name, the time, one round sign at the corner, and the
   whole cell dyed in the drawer's chip colours (Onur, 14.10.2026:
   "more readable when we dyed the whole cell"): done green (red when
   its fee is not fully paid), to record yellow, not coming red and
   struck through, cancelled grey and struck through. Still to come
   stays white; Geliyor is its sign. */
.cell {
  position: absolute;
  border-radius: 10px;
  padding: .35rem 2rem .3rem .6rem;
  background: var(--paper);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: .05rem;
  min-height: 1.3rem;
  box-shadow: 0 1px 0 rgba(0,0,0,.04);
}
.cell > * { flex-shrink: 0; }
.cell.short { padding-top: .1rem; padding-bottom: 0; }
.cell.short .gc-sub { display: none; }
.gc-who { font-weight: 600; font-size: .92rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gc-sub { display: flex; align-items: center; gap: .4rem; color: var(--muted); font-size: .8rem; white-space: nowrap; overflow: hidden; }
.time { font-variant-numeric: tabular-nums; font-weight: 600; }
.gc-due { color: var(--bad); font-weight: 650; font-variant-numeric: tabular-nums; }
.gc-warn { color: var(--warn); font-weight: 600; }
.gc-reason { overflow: hidden; text-overflow: ellipsis; }
.gc-mark { display: inline-flex; color: var(--muted); }
.gc-mark .sd-i { width: 13px; height: 13px; }
.gc-mark.warn { color: var(--warn); }
.gc-mark.bad { color: var(--bad); }
.gc-mark.blue { color: var(--blue); }
.gc-sign {
  position: absolute; top: .4rem; right: .4rem; width: 22px; height: 22px; border-radius: 50%;
  display: grid; place-items: center; background: var(--chip); color: var(--muted);
}
.gc-sign .sd-i { width: 13px; height: 13px; stroke-width: 2.8; }
.gc-sign.st-planned { background: transparent; box-shadow: inset 0 0 0 2px #c9cdd2; }
.gc-sign.st-confirmed { background: var(--ok-bg); color: var(--ok); }
.gc-sign.st-done { background: var(--ok); color: #fff; }
.gc-sign.st-declined { background: var(--bad); color: #fff; }
.gc-sign.st-unrecorded, .gc-sign.st-partial { background: var(--orange); color: #fff; }
.gc-sign.st-paused { background: var(--paper); }
.cell.narrow { padding-left: .45rem; padding-right: 1.3rem; }
.cell.narrow .gc-sign { width: 16px; height: 16px; top: .35rem; right: .25rem; }
.cell.narrow .gc-sign .sd-i { width: 10px; height: 10px; }
.cell.st-done, .unplanned-cell { background: var(--ok-bg); color: var(--ok); }
.cell.st-done .gc-sub, .unplanned-cell .gc-sub { color: #3d7a4c; }
/* Done but its fee not fully paid (Onur, 15.10.2026): the done tick
   stays, the cell takes the red that matches the done green. */
.cell.st-done.unpaid { background: var(--bad-bg); color: var(--bad); }
.cell.st-done.unpaid .gc-sub { color: #b4504c; }
/* No edge since 03.11.2026 (Onur: "the indicators on the right top
   corner does the trick"); until then an orange inset edge. */
.cell.st-unrecorded, .cell.st-partial { background: var(--warn-bg); color: var(--warn); }
.cell.st-unrecorded .gc-sub, .cell.st-partial .gc-sub { color: #9a6b12; }
.cell.st-declined { background: var(--bad-bg); color: var(--bad); }
.cell.st-declined .gc-sub { color: #b4504c; }
.cell.st-cancelled, .cell.st-paused { background: var(--chip); color: var(--muted); }
.cell.st-cancelled .gc-who, .cell.st-declined .gc-who { text-decoration: line-through; font-weight: 400; }
/* A birthday within a week either side (Onur, 23.10.2026; until
   03.11.2026 the day itself only, the week a small cake): a seans that
   is still on takes the indigo of the picture Onur chose and its cake,
   drawn left of the round sign and set low — 30% of its height below
   the middle, the plate under the cell's edge. The indigo is uneven, a
   soft glow by the cake and deeper at the far corner (Onur, 03.11.2026:
   "subtle, smooth uneven degrations", then "a little darker"). Only
   the colours change: the name, time, amount and marks keep their
   places and the room of any other cell, and the cake sits under them
   (Onur, same day, of a white pill the amount had on the indigo: "not
   to change the content allocations in the cards. just adjust the
   color"). The sign still says where the seans stands. A seans that is
   off keeps a small cake beside its marks. Too narrow or too short for
   the cake: the indigo. */
.gc-cake .cake-mark { width: 17px; height: 17px; }
.cell.bday, .cell.bday.unpaid {
  background:
    radial-gradient(75% 120% at 86% 88%, rgba(140, 154, 250, .42), rgba(140, 154, 250, 0) 70%),
    radial-gradient(85% 150% at 0% 0%, rgba(17, 21, 78, .62), rgba(17, 21, 78, 0) 72%),
    radial-gradient(60% 130% at 38% 125%, rgba(112, 84, 204, .34), rgba(112, 84, 204, 0) 70%),
    linear-gradient(160deg, #283380 0%, #313f95 52%, #3a49a3 100%);
  color: #fff;
}
.cell.bday .gc-who, .cell.bday .gc-sub { position: relative; z-index: 1; }
.cell.bday .gc-sub, .cell.bday.unpaid .gc-sub { color: #dfe3ff; }
.cell.bday .gc-due { color: #ffb3ad; }
.cell.bday .gc-warn, .cell.bday .gc-mark.warn { color: #ffd47a; }
.cell.bday .gc-mark, .cell.bday .gc-mark.blue { color: #dfe3ff; }
.cell.bday .gc-mark.bad { color: #ffb3ad; }
.cell.bday .gc-sign.st-planned { box-shadow: inset 0 0 0 2px rgba(255, 255, 255, .75); }
.bday-art {
  position: absolute; right: 1.75rem; top: 50%; transform: translateY(-20%);
  width: 50px; height: 50px; pointer-events: none;
}
.cell.bday.narrow .bday-art, .cell.bday.short .bday-art { display: none; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

.chip {
  display: inline-block;
  padding: 0 .4rem;
  border-radius: 999px;
  font-size: .72rem;
  line-height: 1.4;
  background: var(--grey-bg);
  white-space: nowrap;
}
.chip-icon { font-weight: 700; }
.chip.paid, .chip.recorded, .chip.confirmed { background: var(--ok-bg); color: var(--ok); }
.chip.declined, .chip.bad { background: var(--bad-bg); color: var(--bad); }
.chip.cancelled, .chip.paused { background: #e3e1dc; color: #4f524f; }
.chip.planned { background: var(--blue-bg); color: var(--blue); }
.chip.flag { background: var(--warn-bg); color: var(--warn); }

@media (max-width: 720px) {
  .who { display: none; }
  :root { --hour-h: 56px; }
}

/* ── Tappable and draggable cells ───────────────────────── */
.cell { user-select: none; }
.cell.tappable { cursor: pointer; }
/* A seans on the move: lifted, the new time in its head, snapping to
   five minutes; outside its column it says why it cannot land there. */
.cell.dragging {
  z-index: 4; cursor: grabbing; opacity: .93;
  box-shadow: 0 10px 28px rgba(0,0,0,.28);
  transition: none;
}
.cell.dragging .time { color: var(--brand-deep); }
.cell.dragging.outside { outline: 2px solid var(--bad); }
.drag-hint {
  position: absolute; left: .35rem; right: .35rem; bottom: .1rem;
  font-size: .7rem; font-weight: 700; color: var(--bad);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.move-line { margin: 0; font-size: 1.15rem; }
.time-input { width: auto; padding: .35rem .5rem; }
.cell.tappable:hover, .cell.tappable:focus-visible {
  box-shadow: 0 0 0 2px var(--ink); opacity: 1;
  outline: none;
  z-index: 1;
}
button.link {
  border: 0; padding: 0; background: none; color: var(--brand-deep);
  text-decoration: underline; cursor: pointer; font: inherit;
}

/* ── Sheets ─────────────────────────────────────────────── */
.sheet-backdrop {
  position: fixed; inset: 0; z-index: 10;
  background: rgba(20, 22, 21, .35);
  display: flex; align-items: flex-end; justify-content: center;
  padding: 0 0 env(safe-area-inset-bottom);
}
.sheet {
  width: min(40rem, 100%);
  max-height: 88vh;
  overflow: auto;
  background: var(--paper);
  border-radius: 14px 14px 0 0;
  box-shadow: 0 -8px 30px rgba(0,0,0,.15);
  display: flex; flex-direction: column;
}
/* On a wide screen every sheet is a drawer from the right: the day
   stays in view, lightly dimmed, while the desk reads or acts. */
@media (min-width: 900px) {
  .sheet-backdrop {
    align-items: stretch; justify-content: flex-end;
    background: rgba(20, 22, 21, .18);
  }
  .sheet {
    width: min(36rem, 100%); height: 100%; max-height: none;
    border-radius: 0; box-shadow: -8px 0 30px rgba(0,0,0,.15);
  }
}
.sheet-head {
  display: flex; align-items: center; gap: .75rem;
  padding: .8rem 1rem; border-bottom: 1px solid var(--line);
  position: sticky; top: 0; background: var(--paper);
}
.sheet-title { flex: 1; font-weight: 600; }
.sheet-close { border: 0; background: none; font-size: 1.4rem; line-height: 1; padding: 0 .25rem; }
.sheet-body { padding: 1rem; display: grid; gap: .75rem; }
.sheet-flags { margin: 0; display: flex; gap: .3rem; flex-wrap: wrap; }
.sheet-actions { display: flex; gap: .5rem; justify-content: flex-end; flex-wrap: wrap; margin-top: .25rem; }
button.big { padding: .7rem 1.1rem; font-weight: 600; }
button.danger { border-color: var(--bad); color: var(--bad); }
button.danger:hover:not(:disabled) { background: var(--bad-bg); }
.button-link {
  display: inline-block; padding: .45rem .9rem; border: 1px solid var(--line);
  border-radius: 8px; color: var(--ink); text-decoration: none; background: var(--paper);
}
.menu-client { border: 1px solid var(--line); border-radius: 10px; padding: .6rem .75rem; display: grid; gap: .5rem; }
.menu-client-head { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; }
.menu-actions { display: flex; gap: .4rem; flex-wrap: wrap; }
.menu-foot { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
.menu-foot button.on { color: var(--ok); border-color: var(--ok); }
.this-seans { margin: 0 0 .4rem; display: flex; align-items: baseline; gap: .4rem .9rem; flex-wrap: wrap; }
.due-line { margin: 0; display: flex; align-items: baseline; gap: .5rem; flex-wrap: wrap; }
.due-line b { font-size: 1.25rem; }
.choice-row { display: flex; gap: .4rem; flex-wrap: wrap; align-items: center; }
/* ── The pay sheet (A3, Onur 13.10.2026) ───────────────────
   The first screen in the new look, from Onur's samples: a cool grey
   ground, one white card holding the figure, the way the money came
   and a dark button, and the account in a grey strip at its foot.
   Scoped to .pay-sheet until the rest of masa moves over, one screen
   at a time; the font stays the app's until then. Nothing on the card
   changes size between "Bugün alınacak" and "Farklı tutar gir": the
   figure is the field, the chip holds both its words, the line under
   the figure is one line, and the method row dims instead of leaving. */
.sheet.pay-sheet, .sheet.seans-sheet {
  --p-ground: #f2f3f5;
  --p-card: #ffffff;
  --p-ink: #16191c;
  --p-soft: #5f656b;
  --p-line: #e6e8eb;
  --p-chip: #e9ebee;
  --p-chip-hover: #dfe2e6;
  --p-strip: #f8f9fa;
  --p-dark: #17191b;
  --p-cur: #a9afb5;
  background: var(--p-ground);
  color: var(--p-ink);
}
.pay-sheet .sheet-head { background: var(--p-ground); border-bottom: 0; padding: 1rem 1.1rem .25rem; }
.pay-sheet .sheet-title .muted { font-weight: 400; }
.pay-sheet .sheet-close {
  width: 2.2rem; height: 2.2rem; padding: 0; border-radius: 50%;
  background: var(--p-card); display: grid; place-items: center;
}
.pay-sheet .sheet-body { padding: .75rem 1rem 1.25rem; gap: .5rem; }
.pay-sheet button { font-variant-numeric: tabular-nums; }
.pay-sheet button:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
.pay-card { background: var(--p-card); border-radius: 16px; overflow: hidden; }
.pay-main {
  padding: 1rem 1.15rem 1.15rem; display: grid; gap: .8rem;
  grid-template-columns: minmax(0, 1fr);
}
.pay-main code { overflow-wrap: anywhere; }
.pay-head { display: flex; justify-content: space-between; align-items: center; gap: .5rem; }
.pay-head small { color: var(--p-soft); font-size: .88rem; }
.pay-sheet button.pay-edit {
  display: inline-grid; border: 0; border-radius: 999px; background: var(--p-chip);
  padding: .32rem .85rem; font-size: .84rem; color: var(--p-ink);
}
.pay-sheet button.pay-edit:hover:not(:disabled) { background: #dfe2e6; }
.pay-edit > span { grid-area: 1 / 1; text-align: center; }
.pay-edit > span.off { visibility: hidden; }
.pay-figure-wrap { display: grid; gap: .35rem; grid-template-columns: minmax(0, 1fr); }
.pay-figure-row {
  display: flex; align-items: baseline; gap: .5rem; padding-bottom: .3rem;
  box-shadow: inset 0 -2px 0 transparent; transition: box-shadow .15s;
}
.pay-figure-row.editing { box-shadow: inset 0 -2px 0 var(--brand); }
/* The field takes the width of its text: the hidden copy after it sets
   the cell's width (2px more, for the caret), and the input, which
   asks for no width of its own, fills the cell. */
.pay-figure {
  display: inline-grid; grid-template-columns: minmax(0, auto);
  min-width: 0; max-width: 100%; overflow: hidden;
}
.pay-figure::after {
  content: attr(data-value); padding-right: 2px; visibility: hidden; white-space: pre;
}
.pay-figure::after, .pay-figure input { grid-area: 1 / 1; }
.pay-figure::after, .pay-figure input, .pay-cur {
  font-size: 2.5rem; line-height: 1.15; letter-spacing: -.02em;
  font-weight: 700; font-variant-numeric: tabular-nums;
}
.pay-figure input {
  width: 0; min-width: 100%; border: 0; padding: 0; margin: 0; border-radius: 0;
  background: transparent; color: var(--p-ink); outline: none; caret-color: var(--brand);
}
.pay-figure input[readonly] { pointer-events: none; }
.pay-figure input::placeholder { color: var(--p-cur); }
.pay-figure input::selection { background: #bfe9f9; }
.pay-cur { font-weight: 600; color: var(--p-cur); flex-shrink: 0; }
.pay-note {
  margin: 0; font-size: .88rem; color: var(--p-soft); min-height: 1.35em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pay-note.bad { color: var(--bad); }
.pay-note.ok { color: var(--ok); }
.pay-methods {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; padding: 4px;
  background: var(--p-chip); border-radius: 12px; transition: opacity .15s;
}
.pay-methods.dim { opacity: .45; }
.pay-sheet .pay-methods button {
  border: 0; border-radius: 9px; background: none; padding: .55rem .3rem; color: var(--p-ink);
  font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pay-sheet .pay-methods button:disabled { opacity: 1; } /* the row dims as one */
.pay-sheet .pay-methods button.off { opacity: .4; }
/* "Havale / EFT" is "Havale" where three tiles would cut it. */
@media (max-width: 360px) { .pay-methods .pay-wide { display: none; } }
/* The chosen way is told by its tile, not its weight: a bolder word
   could wrap and grow the row. */
.pay-sheet .pay-methods button[aria-pressed="true"] {
  background: var(--p-card); box-shadow: 0 1px 2px rgba(0, 0, 0, .1);
}
.pay-sheet button.pay-go {
  width: 100%; border: 0; border-radius: 14px; padding: .95rem 1rem;
  background: var(--p-dark); color: #fff; font-size: 1.02rem; font-weight: 600;
}
.pay-sheet button.pay-go:hover:not(:disabled) { background: #000; }
.pay-sheet button.pay-go:disabled { opacity: .35; }
.pay-go span { font-weight: 400; color: #a9b0b6; white-space: nowrap; }
.pay-strip {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  background: var(--p-strip); border-top: 1px solid var(--p-line);
}
.pay-strip > div { padding: .6rem 1.15rem; display: grid; min-width: 0; }
.pay-strip > div + div { border-left: 1px solid var(--p-line); }
.pay-strip span {
  font-size: .68rem; font-weight: 600; letter-spacing: .07em; text-transform: uppercase;
  color: var(--p-soft);
}
.pay-strip b { font-size: .98rem; font-variant-numeric: tabular-nums; }
.pay-strip .neg span, .pay-strip .neg b { color: var(--bad); }
.pay-strip .pos span, .pay-strip .pos b { color: var(--ok); }
.pay-sheet button.pay-link {
  justify-self: center; border: 0; background: none; padding: .35rem .5rem;
  color: var(--p-soft); font-size: .92rem; text-decoration: underline; text-underline-offset: 3px;
}
.pay-sheet button.pay-link:hover:not(:disabled) { color: var(--p-ink); }
/* Ver's sheets (PayoutSheets.tsx): the pay sheet's card under the
   drawer's head — two ways the money left, the warning when a seans
   is still to be recorded, a quiet line under it all. */
.pay-methods.two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.payout-sheet .pay-methods button, .payout-sheet button.pay-go { text-align: center; }
.pay-warn {
  margin: 0; background: var(--warn-bg); color: var(--warn); border-radius: 12px;
  padding: 10px 14px; font-size: .88rem; font-weight: 500;
}
.pay-foot { margin: 0; padding: 0 4px; color: var(--p-soft); font-size: .84rem; }

/* ── The seans drawer (v3, Onur 13.10.2026) ────────────────
   The pay sheet's ground and cards, the mockup's sizes: a head that is
   the time (name, series, chips), a white money card whose row of round
   buttons keeps one height in every state, and the glance cards under
   it. The sheets it opens (WhatsApp onayı, Seans bilgisi, Seanslar ve
   ödemeler, Notlar) wear the same. Every class is sd- and scoped here,
   so nothing leaks into the grid's own .chip, .card or .name. */
.seans-sheet button {
  border: 0; border-radius: 0; background: none; padding: 0; color: inherit; text-align: inherit;
}
.seans-sheet button:focus-visible, .seans-sheet a:focus-visible {
  outline: 2px solid var(--brand); outline-offset: 2px;
}
.seans-sheet button:disabled { opacity: .5; }
.seans-sheet button[aria-busy="true"] { opacity: 1; }
.seans-sheet .sheet-body { padding: 10px 16px 20px; gap: 12px; }
.sd-i {
  width: 1.05em; height: 1.05em; flex-shrink: 0; fill: none; stroke: currentColor;
  stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
}
.sd-ell { min-width: 0; overflow: hidden; text-overflow: ellipsis; }

/* The head: the time */
.seans-sheet .sd-head { display: grid; gap: 2px; padding: 20px 20px 4px; }
.seans-sheet .sd-who { display: flex; align-items: center; gap: 12px; min-width: 0; }
.seans-sheet .sd-name {
  display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; margin: 0;
  font-size: inherit; font-weight: inherit; text-align: left;
}
.seans-sheet .sd-name b {
  font-size: 1.6rem; line-height: 1.15; letter-spacing: -.015em; font-weight: 700;
  overflow-wrap: anywhere; min-width: 0;
}
.seans-sheet .sd-name .sd-i { color: var(--p-cur); width: 1.25rem; height: 1.25rem; }
/* A group: each name its own way to the child's card. */
.seans-sheet .sd-names { flex-wrap: wrap; column-gap: 14px; row-gap: 2px; }
.seans-sheet .sd-names button { display: inline-flex; align-items: center; gap: 4px; min-width: 0; }
.seans-sheet .sd-round {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  display: grid; place-items: center; background: var(--p-card);
}
.seans-sheet .sd-round:hover:not(:disabled) { background: var(--p-chip); }
/* The read on its way (Onur 15.10.2026): a ring left of ×. Idle, it
   keeps its room, so nothing beside it moves when it goes. */
.seans-sheet .sd-spin {
  width: 20px; height: 20px; flex-shrink: 0; border-radius: 50%; visibility: hidden;
  border: 2px solid var(--p-chip-hover); border-top-color: var(--p-soft);
}
.seans-sheet .sd-spin.on { visibility: visible; animation: spin .8s linear infinite; }
@media (prefers-reduced-motion: reduce) { .seans-sheet .sd-spin.on { animation-duration: 2.4s; } }
/* As a bottom sheet the drawer holds its full height from the start:
   grown to its cards, it would rise under the desk's finger when Son
   seanslar and Notlar land. */
@media (max-width: 899.98px) { .sheet.sd-drawer { height: 88vh; } }
.seans-sheet .sd-when {
  justify-self: start; display: inline-flex; align-items: center; gap: 4px; max-width: 100%;
  color: var(--p-soft); font-size: 1rem;
}
.seans-sheet .sd-when .sd-i { color: var(--p-cur); }
.seans-sheet .sd-when:hover:not(:disabled) { color: var(--p-ink); }
.seans-sheet .sd-chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0 0; min-width: 0; }

.seans-sheet .sd-chip {
  display: inline-flex; align-items: center; gap: 7px; max-width: 100%; min-width: 0;
  border-radius: 999px; padding: 7px 14px; font-size: .9rem; line-height: 1.4;
  white-space: nowrap; background: var(--p-chip);
}
.seans-sheet button.sd-chip:not(.on, .pressed, .stop):hover:not(:disabled) { background: var(--p-chip-hover); }
.seans-sheet a.sd-chip { color: inherit; text-decoration: none; }
.seans-sheet a.sd-chip:hover { background: var(--p-chip-hover); }
.seans-sheet .sd-head .sd-chip { background: var(--p-card); }
.seans-sheet .sd-head button.sd-chip:not(.on, .pressed):hover:not(:disabled) { background: var(--p-chip); }
.seans-sheet .sd-chip.small { padding: 5px 12px; font-size: .84rem; }
.seans-sheet .sd-chip .soft { color: var(--p-soft); }
.seans-sheet .sd-chip .soft.ok { color: var(--ok); }
.seans-sheet .sd-chip .soft.bad { color: var(--bad); font-weight: 600; }
.seans-sheet .sd-chip .soft.ask { color: var(--blue); font-weight: 600; }
.seans-sheet .sd-chip.on, .seans-sheet .sd-chip.done,
.seans-sheet .sd-chip.credit { background: var(--ok-bg); color: var(--ok); font-weight: 600; }
.seans-sheet .sd-chip.debt,
.seans-sheet .sd-chip.bad { background: var(--bad-bg); color: var(--bad); font-weight: 600; }
.seans-sheet .sd-chip.warn { background: var(--warn-bg); color: var(--warn); }
/* The status chip: tinted, never white — the white chips are the ones
   that act. */
.seans-sheet .sd-chip.off { background: var(--p-chip); color: var(--p-soft); }
.seans-sheet .sd-chip.state { font-weight: 600; }
.seans-sheet .sd-chip.pressed { background: var(--p-dark); color: #fff; }
/* A birthday within a week of the seans, in the grid's indigo. */
.seans-sheet .sd-chip.bday {
  background: linear-gradient(165deg, #2b378a, #3f4ea8); color: #fff; font-weight: 600;
}
.seans-sheet .sd-chip .cake-mark { width: 1.25em; height: 1.25em; flex-shrink: 0; }
.seans-sheet button.sd-chip.pressed:hover:not(:disabled) { background: #000; }
.seans-sheet button.sd-chip.on:disabled, .seans-sheet .sd-chip.pressed:disabled { opacity: .7; }
.sd-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; flex-shrink: 0; }

/* The money card */
.seans-sheet .sd-card { background: var(--p-card); border-radius: 16px; min-width: 0; }
/* The fee and the round buttons on one line: the buttons at the right,
   their circles level with the number, their labels beside the account
   chips under it. Everything else (warnings, İptal's reasons) runs the
   full width below. */
.seans-sheet .sd-money {
  padding: 16px 18px 18px; display: grid; gap: 8px 12px;
  grid-template-columns: minmax(0, 1fr) auto;
}
.seans-sheet .sd-money > * { grid-column: 1 / -1; }
.seans-sheet .sd-money > .sd-label { grid-row: 1; grid-column: 1; margin-bottom: -6px; }
.seans-sheet .sd-money > .sd-figure, .seans-sheet .sd-money > .sd-status { grid-row: 2; grid-column: 1; }
.seans-sheet .sd-money > .sd-acct, .seans-sheet .sd-money > .sd-sub { grid-row: 3; grid-column: 1; }
/* The circle's middle (2px padding + half of 48px) on the number's
   middle: half its line box. */
.seans-sheet .sd-money > .sd-acts {
  grid-row: 2 / span 2; grid-column: 2; align-self: start; margin-top: calc(1.4rem - 26px);
}
.seans-sheet .sd-money:has(> .sd-figure.none) > .sd-acts { margin-top: calc(.975rem - 26px); }
.seans-sheet .sd-money:has(> .sd-status) > .sd-acts { margin-top: calc(.96rem - 26px); }
/* No figures for this login: the buttons beside the label. */
.seans-sheet .sd-money:not(:has(> .sd-figure, > .sd-status)) > .sd-acts {
  grid-row: 1 / span 3; align-self: center; margin-top: 0;
}
.seans-sheet .sd-pad { padding: 14px 18px 16px; display: grid; gap: .55rem; }
.seans-sheet .sd-label { justify-self: start; color: var(--p-soft); font-size: .9rem; }
.seans-sheet button.sd-kid {
  display: inline-flex; align-items: center; gap: 4px; max-width: 100%;
  color: var(--p-ink); font-size: 1.05rem;
}
.seans-sheet .sd-kid b { font-weight: 700; overflow-wrap: anywhere; }
.seans-sheet .sd-kid .sd-i { color: var(--p-cur); }
.seans-sheet .sd-figure {
  font-size: 2.5rem; line-height: 1.12; letter-spacing: -.02em; font-weight: 700;
  font-variant-numeric: tabular-nums; overflow-wrap: anywhere;
}
.seans-sheet .sd-figure .sd-cur { color: var(--p-cur); font-weight: 600; }
.seans-sheet .sd-figure.none { font-size: 1.5rem; line-height: 1.3; color: var(--p-soft); }
.seans-sheet .sd-status { font-size: 1.6rem; line-height: 1.2; font-weight: 700; }
.seans-sheet .sd-sub { margin: 0; color: var(--p-soft); overflow-wrap: anywhere; }
.seans-sheet .sd-acct { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 2px; min-width: 0; }
.seans-sheet .sd-warn { margin: 2px 0 0; font-size: .86rem; color: var(--warn); }
.seans-sheet .sd-acts { display: flex; align-items: flex-start; gap: 2px; }
.seans-sheet .sd-act {
  display: grid; justify-items: center; align-content: start; gap: 1px; min-width: 64px;
  padding: 2px 6px; border-radius: 12px; text-align: center;
}
.sd-act .sd-circle {
  width: 48px; height: 48px; border-radius: 50%; display: grid; place-items: center;
  background: var(--p-chip); margin-bottom: 4px; transition: background .12s;
}
.sd-act .sd-circle .sd-i { width: 20px; height: 20px; }
.seans-sheet .sd-act:hover:not(:disabled) .sd-circle { background: var(--p-chip-hover); }
.sd-act b { font-weight: 600; font-size: .8rem; line-height: 1.2; white-space: nowrap; }
.sd-act small {
  color: var(--p-soft); font-size: .74rem; line-height: 1.2; white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.sd-act.primary .sd-circle { background: var(--p-dark); color: #fff; }
.seans-sheet .sd-act.primary:hover:not(:disabled) .sd-circle { background: #000; }

/* İptal: its reasons open under the fee while the buttons step aside
   (on a phone in smaller chips). */
.seans-sheet .sd-why {
  margin-top: 6px; padding-top: 12px; border-top: 1px solid var(--p-line);
  display: grid; gap: 8px; align-content: start;
}
.seans-sheet .sd-why.alone { margin-top: 0; padding-top: 0; border-top: 0; }
.sd-why-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.sd-why-top b { font-size: .95rem; }
.seans-sheet .sd-why .sd-chips { margin: 0; gap: 6px; }
.sd-who-cancels {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: .84rem; color: var(--p-soft);
}
.sd-seg { display: inline-flex; gap: 2px; padding: 3px; border-radius: 999px; background: var(--p-chip); }
.seans-sheet .sd-seg button { padding: 4px 11px; border-radius: 999px; color: var(--p-soft); }
.seans-sheet .sd-seg button.on {
  background: var(--p-card); color: var(--p-ink); font-weight: 600; box-shadow: 0 1px 2px rgba(0, 0, 0, .1);
}
.seans-sheet .sd-why .sd-chip .sd-i { color: var(--p-soft); }
.seans-sheet .sd-why .sd-chip.pressed .sd-i { color: inherit; }
/* Diğer: the reason typed in place, and the İptal et it needs. */
.seans-sheet .sd-other { display: flex; gap: 6px; align-items: center; min-width: 0; }
.seans-sheet .sd-other input {
  flex: 1; min-width: 0; padding: 5px 14px; border: 1px solid var(--p-line); border-radius: 999px;
  background: var(--p-card); color: var(--p-ink); font-size: .9rem; line-height: 1.4; outline: none;
}
.seans-sheet .sd-other input:focus { border-color: var(--brand); box-shadow: 0 0 0 1px var(--brand); }
.seans-sheet .sd-other input::placeholder { color: var(--p-cur); }
.seans-sheet .sd-chip.stop { background: var(--bad-bg); color: var(--bad); font-weight: 600; }
.seans-sheet button.sd-chip.stop:hover:not(:disabled) { background: #f6d6d2; }

/* The glance: the last seanslar, the newest note */
.seans-sheet .sd-list { padding: 14px 18px 8px; }
.sd-list-head {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding-bottom: 10px; border-bottom: 1.5px dashed var(--p-line);
}
.sd-list-head h3 { margin: 0; font-size: 1rem; font-weight: 700; min-width: 0; overflow-wrap: anywhere; }
.sd-rows { list-style: none; margin: 0; padding: 0; }
.sd-rows li {
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 12px; align-items: center;
  padding: 9px 0;
}
.sd-rows li + li { border-top: 1px solid var(--p-line); }
.sd-ic {
  width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center;
  background: var(--p-ground);
}
.sd-ic.x { color: var(--bad); background: var(--bad-bg); }
.sd-rows li.new .sd-ic { color: var(--ok); background: var(--ok-bg); }
.sd-t { display: grid; min-width: 0; }
.sd-t small { overflow-wrap: anywhere; }
.sd-rows b { font-weight: 600; font-size: .95rem; }
.sd-rows small { color: var(--p-soft); font-size: .84rem; }
.sd-r { display: grid; justify-items: end; text-align: right; font-variant-numeric: tabular-nums; }
.sd-r small.owe { color: var(--bad); font-weight: 600; }
.sd-month + .sd-month { margin-top: 10px; }
.sd-month h3 {
  margin: 0; padding-bottom: 6px; border-bottom: 1.5px dashed var(--p-line);
  font-size: .78rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  color: var(--p-soft);
}
.sd-note { padding: 10px 0 8px; display: grid; gap: 3px; min-width: 0; }
.sd-note p { margin: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.sd-note p.clamp {
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.sd-note small { color: var(--p-soft); font-size: .84rem; }
.sd-notes { list-style: none; margin: 0; padding: 0; }
.sd-notes li + li { border-top: 1px solid var(--p-line); }
.seans-sheet .sd-quiet { margin: 0; padding: 8px 0; color: var(--p-soft); font-size: .9rem; overflow-wrap: anywhere; }
.seans-sheet .sd-pad .sd-quiet { padding: 0; }
.seans-sheet button.sd-more {
  display: block; margin: 0 0 8px; color: var(--p-soft); font-size: .88rem;
  text-decoration: underline; text-underline-offset: 3px;
}
.seans-sheet button.sd-more:hover:not(:disabled) { color: var(--p-ink); }
.seans-sheet .sd-foot {
  justify-self: center; padding: 4px 8px; color: var(--p-soft); font-size: .9rem;
  text-decoration: underline; text-underline-offset: 3px;
}
.seans-sheet .sd-foot:hover { color: var(--p-ink); }

/* The sheets over the drawer */
.seans-sheet .sd-back { background: var(--p-card); }
.seans-sheet .sd-title { margin: 10px 0 0; font-size: 1.35rem; line-height: 1.2; font-weight: 700; }
.seans-sheet .sd-card-title { margin: 0; font-size: 1rem; font-weight: 700; }
.seans-sheet button.sd-go {
  width: 100%; border-radius: 14px; padding: .9rem 1rem; margin-top: .25rem;
  background: var(--p-dark); color: #fff; font-weight: 600; text-align: center;
}
.seans-sheet button.sd-go:hover:not(:disabled) { background: #000; }
.seans-sheet button.sd-go:disabled { opacity: .35; }
.seans-sheet .sd-pad .sec:first-child { margin-top: 0; }

@media (max-width: 600px) {
  .seans-sheet .sd-head { padding: 16px 16px 4px; }
  .seans-sheet .sheet-body { padding: 8px 12px 16px; }
  .seans-sheet .sd-name b { font-size: 1.4rem; }
  .seans-sheet .sd-figure { font-size: 2rem; }
  .seans-sheet .sd-money { padding: 14px 14px 16px; }
  .seans-sheet .sd-list { padding: 12px 14px 6px; }
  /* Narrower buttons, labels on two lines, so the row still fits next to
     the fee; the account chips then take the full width under them. */
  .seans-sheet .sd-money > .sd-acct { grid-row: auto; grid-column: 1 / -1; }
  .seans-sheet .sd-money > .sd-acts { margin-top: calc(1.12rem - 24px); }
  .seans-sheet .sd-money:has(> .sd-figure.none) > .sd-acts { margin-top: calc(.975rem - 24px); }
  .seans-sheet .sd-money:has(> .sd-status) > .sd-acts { margin-top: calc(.96rem - 24px); }
  .seans-sheet .sd-acts { gap: 0; }
  .seans-sheet .sd-act { width: 62px; min-width: 0; padding: 2px; }
  .sd-act b { white-space: normal; }
  .sd-act .sd-circle { width: 44px; height: 44px; }
  .seans-sheet .sd-why { gap: 6px; }
  .seans-sheet .sd-why .sd-chips { gap: 4px; }
  .seans-sheet .sd-why .sd-chip.small { padding: 4px 10px; font-size: .8rem; }
  .seans-sheet .sd-other input { padding: 4px 12px; font-size: .86rem; }
  .seans-sheet .sd-why .sd-seg { padding: 2px; }
  .seans-sheet .sd-why .sd-seg button { padding: 3px 10px; }
}
/* The narrowest phones: a smaller number, so "TL" stays on its line. */
@media (max-width: 380px) {
  .seans-sheet .sd-figure { font-size: 1.7rem; white-space: nowrap; }
  .seans-sheet .sd-money > .sd-acts { margin-top: calc(.952rem - 24px); }
}
@media (prefers-reduced-motion: reduce) { .sd-act .sd-circle { transition: none; } }

button.choice { border-radius: 999px; padding: .35rem .8rem; }
button.choice.on { background: var(--brand); border-color: var(--brand); color: var(--brand-ink); }
.text {
  width: 100%; padding: .5rem .6rem; border: 1px solid var(--line); border-radius: 8px;
  background: var(--paper); font: inherit;
}
textarea.text { resize: vertical; }
.check { display: flex; align-items: flex-start; gap: .5rem; }
.check.inline { display: inline-flex; margin-left: .5rem; }

/* ── The drawer's detail (1.5) ──────────────────────────── */
.detail { display: grid; gap: .3rem; }
.sec {
  margin: .45rem 0 0; font-size: .72rem; font-weight: 700;
  letter-spacing: .05em; text-transform: uppercase; color: var(--muted);
}
.kv { display: grid; grid-template-columns: 6.5rem 1fr; gap: .3rem .6rem; font-size: .92rem; }
.kv > .k { color: var(--muted); }
.lines { margin: 0; padding: 0; list-style: none; display: grid; gap: .2rem; font-size: .92rem; }
.lines li, .lines > div { display: flex; gap: .45rem; flex-wrap: wrap; align-items: baseline; }
.note-text { white-space: pre-wrap; }
.money-grid { display: flex; gap: .3rem 1rem; flex-wrap: wrap; font-size: .92rem; }
.money-grid b { font-size: 1.02rem; }
.amount-neg { color: var(--bad); }
.wa-reply { background: var(--blue-bg); border-radius: 8px; padding: .3rem .55rem; }
.chip.note { background: var(--warn-bg); color: var(--warn); max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 600px) {
  .kv { grid-template-columns: 1fr; }
  .kv > .k { margin-top: .3rem; }
}

/* ── The client form ────────────────────────────────────── */
.form { display: grid; gap: .6rem; }
.form-row { display: grid; grid-template-columns: 8rem 1fr; gap: .5rem; align-items: center; }
.form-row.col { grid-template-columns: 1fr; }
.form-row select { padding: .4rem .5rem; border: 1px solid var(--line); border-radius: 8px; background: var(--paper); }
.contacts { border: 1px solid var(--line); border-radius: 10px; padding: .5rem .75rem .75rem; display: grid; gap: .5rem; }
.contacts legend { font-weight: 600; padding: 0 .3rem; }
.contact-row { display: grid; grid-template-columns: 1.4fr 1fr 1.2fr auto; gap: .4rem; align-items: center; }
.radio { display: flex; align-items: center; gap: .4rem; }
.small-link { border: 0; background: none; color: var(--brand-deep); text-decoration: underline; cursor: pointer; justify-self: start; font-size: .85rem; }
@media (max-width: 600px) {
  .form-row { grid-template-columns: 1fr; }
  .contact-row { grid-template-columns: 1fr 1fr; }
}

/* ── Toasts ─────────────────────────────────────────────── */
.toasts {
  position: fixed; right: 16px; bottom: 16px; z-index: 20;
  display: grid; gap: .4rem; width: min(26rem, calc(100vw - 32px));
}
.toast {
  display: flex; align-items: center; gap: .5rem;
  padding: .6rem .8rem; border-radius: 10px;
  background: var(--ink); color: #fff; box-shadow: 0 6px 20px rgba(0,0,0,.2);
  font-size: .95rem;
}
.toast.ok { background: #1f4d3a; }
.toast.warn { background: #6b4700; }
.toast.error { background: #7a1f1c; }
.toast-text { flex: 1; }
.toast-action, .toast-close {
  border: 1px solid rgba(255,255,255,.5); background: transparent; color: #fff;
  border-radius: 6px; padding: .2rem .6rem;
}
.toast-close { border: 0; font-size: 1.1rem; padding: 0 .3rem; }

/* ── Yapılacaklar drawer (step 1: the Lore sign-in) ─────── */
.todo-btn { white-space: nowrap; }
.todo-empty { display: grid; gap: .75rem; justify-items: start; }
.todo-empty p { margin: 0; }
.todo-body { display: grid; gap: .75rem; }
.todo-body p { margin: 0; }
.todo-list { list-style: none; margin: 0; padding: 0; display: grid; gap: .5rem; }
.todo-item {
  display: flex; align-items: flex-start; gap: .75rem;
  background: var(--paper); border-radius: 12px; padding: .6rem .9rem;
}
.todo-text { flex: 1; min-width: 0; display: grid; gap: .15rem; }
.todo-title { font-weight: 600; }
.todo-desc { white-space: pre-wrap; overflow-wrap: anywhere; }
.todo-form { display: inline-block; margin-top: .3rem; font-weight: 600; }
.todo-toast {
  display: flex; align-items: center; justify-content: space-between; gap: .75rem;
  background: var(--ok-bg); color: var(--ok); border-radius: 10px; padding: .5rem .9rem;
}

/* ── Seans ekle / Yeni danışan (actions/AddSheets.tsx) ─────
   Two pills after the date; on a phone two round icons that close the
   bar's last row, after Yenile. The sheets wear the pay sheet's card:
   grey fields that light up with the brand ring, the danışman as
   chips with her photo, the segmented rows of the pay sheet, a dark
   button that names what it will do, and under it the fee strip. */
.day-nav .add .add-wide, .day-nav .add .add-narrow { display: contents; }
.day-nav .add .add-narrow { display: none; }
@media (max-width: 600px) {
  .day-nav .add { width: 40px; height: 40px; padding: 0; justify-content: center; order: 2; }
  .day-nav .add .add-wide { display: none; }
  .day-nav .add .add-narrow { display: contents; }
  .day-nav .refresh { order: 1; }
}
.add-sheet .sd-head { padding: 20px 20px 4px; }
.add-sheet .sd-who > .sd-title { margin: 0; }
.add-sheet .sheet-body { gap: 10px; }
.add-sheet .pay-main { gap: 16px; }
.add-sheet .f { display: grid; gap: 7px; min-width: 0; }
.add-sheet .f-label { color: var(--p-soft); font-size: .88rem; display: flex; gap: 8px; align-items: baseline; }
.add-sheet .f-label em { font-style: normal; color: var(--p-cur); font-size: .8rem; }
.add-sheet .f-input {
  display: flex; align-items: center; gap: 9px; min-height: 46px; padding: 0 14px;
  border-radius: 12px; background: var(--p-ground); font-size: 1rem; color: var(--p-ink);
  border: 1.5px solid transparent; min-width: 0; cursor: text;
}
.add-sheet .f-input:focus-within {
  background: var(--p-card); border-color: var(--brand); box-shadow: 0 0 0 3px rgba(0, 176, 236, .16);
}
.add-sheet .f-input .sd-i { color: var(--p-cur); }
.add-sheet .f-input input {
  flex: 1; min-width: 0; width: 100%; border: 0; background: none; padding: 0; margin: 0;
  font: inherit; color: inherit; outline: none; border-radius: 0;
}
.add-sheet .f-input input::placeholder { color: var(--p-cur); }
.add-sheet .f-input input::-webkit-calendar-picker-indicator { opacity: .5; }
.add-sheet .f-input .unit { color: var(--p-cur); font-weight: 600; }
.add-sheet .f-input.num input { font-weight: 700; font-size: 1.15rem; font-variant-numeric: tabular-nums; }
.add-sheet .f-results {
  border-radius: 12px; background: var(--p-card); border: 1px solid var(--p-line);
  overflow: hidden; box-shadow: 0 8px 22px rgba(0, 0, 0, .07); margin-top: -2px; display: grid;
}
.add-sheet button.f-res {
  display: flex; align-items: center; gap: 10px; padding: 10px 14px; font-size: .97rem;
  width: 100%; text-align: left; border: 0; border-radius: 0; background: none; color: inherit;
}
.add-sheet .f-res + .f-res { border-top: 1px solid var(--p-line); }
.add-sheet button.f-res:hover, .add-sheet .f-res.hi { background: #eef8fd; }
.add-sheet .f-res .nm { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.add-sheet .f-res .soft { margin-left: auto; color: var(--p-soft); font-size: .86rem; white-space: nowrap; }
.add-sheet .f-res.off .nm, .add-sheet .f-res.off .avatar { opacity: .6; }
.add-sheet button.f-res.new { color: var(--brand-deep); font-weight: 600; }
.add-sheet .f-res.new .sd-i { width: 1.35rem; height: 1.35rem; margin: 0 2px; }
.add-sheet .f-row { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr); gap: 10px; }
.add-sheet .f-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.add-sheet .f-chips .sd-chip { padding-left: 8px; }
.add-sheet .f-chips .sd-chip .avatar { width: 1.45rem; height: 1.45rem; font-size: .64rem; background: #dcdfe3; }
.add-sheet .f-chips .sd-chip.pressed .avatar { background: rgba(255, 255, 255, .22); color: #fff; }
.add-sheet .f-picked { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 12px; background: var(--p-ground); }
.add-sheet .f-picked > div { display: grid; min-width: 0; flex: 1; }
.add-sheet .f-picked b { font-size: 1.08rem; overflow-wrap: anywhere; }
.add-sheet .f-picked small { color: var(--p-soft); font-size: .86rem; }
.add-sheet .f-picked .sd-chip { background: var(--p-card); }
.add-sheet .avatar.md { width: 2.4rem; height: 2.4rem; font-size: .9rem; }
.add-sheet .f-sep { height: 1px; background: var(--p-line); margin: 0 -1.15rem; }
.add-sheet .f-new { background: #eef8fd; border-radius: 14px; padding: 12px 12px 10px; display: grid; gap: 7px; }
.add-sheet .f-new .f-input { background: var(--p-card); }
.add-sheet .pay-note { white-space: normal; overflow: visible; min-height: 0; }
.add-sheet .dim { opacity: .38; pointer-events: none; }
.add-sheet .pay-ok {
  margin: 0; background: var(--ok-bg); color: var(--ok); border-radius: 12px;
  padding: 10px 14px; font-size: .9rem; font-weight: 500; display: flex; gap: 9px; align-items: flex-start;
}
.add-sheet .pay-ok .sd-i { margin-top: 2px; stroke-width: 2.6; }
.add-sheet .f-warn {
  background: var(--warn-bg); color: var(--warn); border-radius: 12px;
  padding: 11px 14px; font-size: .9rem; font-weight: 500; display: grid; gap: 10px;
}
.add-sheet .f-warn .sd-chip { justify-self: start; background: var(--p-card); color: var(--p-ink); font-weight: 600; }
.add-sheet .pay-strip b { font-size: .95rem; }
.add-sheet .pay-strip > div { padding: .55rem 1.15rem .6rem; }
.add-sheet button.pay-go:disabled { opacity: 1; background: #c9cdd2; color: #fff; }
@media (max-width: 600px) {
  .add-sheet .sd-head { padding: 16px 16px 4px; }
  .add-sheet .f-chips .sd-chip { padding: 6px 12px 6px 7px; font-size: .86rem; }
  .add-sheet .f-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .add-sheet .f-sep { margin: 0 -14px; }
}
